// COPIED from Remote-Leverage/hriq@3b50a234afdd packages/design-system/components/ui/v2/checkbox.tsx by apps/web/scripts/sync-design-system.mjs. Do not edit: change it in Hriq and re-sync.
// @ts-nocheck -- type-checked in Hriq under its own compiler options (the CRM's are stricter).
import { Checkbox as CheckboxV1 } from "@repo/design-system/components/ui/checkbox";
import { cn } from "@repo/design-system/lib/utils";
import type { ComponentProps } from "react";

/**
 * v2 Checkbox — restyles v1's `Checkbox` (`components/ui/checkbox.tsx`)
 * to match the Figma "Remote Leverage — Design System" file, Checkbox
 * component node `364:756`. All Radix behavior (keyboard, focus
 * management, `data-state`, `aria-checked`) stays v1's own — this file
 * only overrides the visual layer.
 *
 * Figma names its states Active/Default/Hover/Focus/Disabled: "Active"
 * is Radix's `data-state=checked` (not the `:active` pointer-down
 * pseudo-class), while Hover/Focus/Disabled map directly to native
 * `hover:`/`focus-visible:`/`disabled:` — browser/Radix-driven states,
 * not props, per the v2 README.
 *
 * v1 already shows a permanent `focus-visible:ring-ring/50` — Figma's
 * Focus state has no ring at all, just a thicker (2px) colored border,
 * so the ring is cancelled with `ring-transparent` (same ring-color
 * slot v1 used, so twMerge drops it) rather than fought via width.
 *
 * Deliberately out of scope: Figma's component set has no
 * `indeterminate` state at all (only Active/Default/Hover/Focus/
 * Disabled) — indeterminate reuses the Active/checked treatment
 * (background, border, icon) below since that's the only "filled"
 * visual language Figma defines, rather than inventing an unspecified
 * color or icon for it.
 */

const controlOverrideClassName = cn(
  // Resting (Default)
  "border-(--v2-checkbox-border-default) shadow-(--v2-checkbox-shadow-resting)",
  // Hover
  "hover:border-(--v2-checkbox-border-hover)",
  // Focus
  "focus-visible:border-(--v2-checkbox-border-focus) focus-visible:border-2 focus-visible:ring-transparent",
  // Active (checked) / indeterminate (see file header note)
  "data-[state=checked]:border-(--v2-checkbox-bg-active) data-[state=checked]:bg-(--v2-checkbox-bg-active) data-[state=checked]:text-(--v2-checkbox-text-active) data-[state=checked]:shadow-(--v2-checkbox-shadow-active)",
  "data-[state=indeterminate]:border-(--v2-checkbox-bg-active) data-[state=indeterminate]:bg-(--v2-checkbox-bg-active) data-[state=indeterminate]:text-(--v2-checkbox-text-active) data-[state=indeterminate]:shadow-(--v2-checkbox-shadow-active)",
  // Disabled
  "disabled:border-(--v2-checkbox-border-default) disabled:bg-(--v2-checkbox-bg-disabled) disabled:opacity-100"
);

type CheckboxProps = ComponentProps<typeof CheckboxV1>;

function Checkbox({ className, ...props }: CheckboxProps) {
  return (
    <CheckboxV1
      className={cn("ds-v2", controlOverrideClassName, className)}
      {...props}
    />
  );
}

export { Checkbox };
export type { CheckboxProps };
